import {
  addCommentEnvelope,
  setCommentError,
  setInlineCommentMode,
  setInlineCommentReference,
} from 'actions';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AnnotatedSuperNodeT, superNodeNeedsSpaceBefore } from '@litlingo/react/dist';
import EnvelopeCollapsableSection from 'components/CollapsableSection/EnvelopeCollapsableSection';
import { debounce } from 'lodash';
import { useParams } from 'react-router-dom';
import { getSelectedCommunicationWithEvents } from 'selectors/communications';
import { getInlineCommentMode, getInlineCommentReference } from 'selectors/envelopeReview';
import { getCommsFullMessageHighlighted } from 'selectors/events';
import { useSelector } from 'store';
import { CommentWithUsers, EnvelopeAction } from 'types';
import CommentsSection from '../CommentsSection';
import InlineCommentsNavigation from './InlineCommentsNavigation';

const InlineCommentSection: React.FC = () => {
  const { envelopeId } = useParams<{ envelopeId: string }>();

  const dispatch = useDispatch();
  const selectedCommunication = useSelector(getSelectedCommunicationWithEvents);

  const inlineCommentMode = useSelector(getInlineCommentMode);
  const commentReference = useSelector(getInlineCommentReference);

  const res = useSelector((state) =>
    getCommsFullMessageHighlighted(
      state,
      commentReference.communicationId || selectedCommunication?.uuid || '',
      'false',
      'false'
    )
  );

  const [comment, setComment] = useState<CommentWithUsers>({
    value: '',
    text: '',
    taggedUsers: [],
  });

  let text = '';
  if (res) {
    const line = res[commentReference.lineNumber];

    if (line) {
      text = line.nodes
        .slice(commentReference.nodes[0], commentReference.nodes[1] + 1)
        .reduce((acc, val, idx, arr) => {
          if (superNodeNeedsSpaceBefore(arr, idx)) {
            return `${acc} ${val.nodes[0].text}`;
          }
          return `${acc}${val.nodes[0].text}`;
        }, '');
    }
  }

  const commentSelection = debounce(
    (
      communicationId: string,
      lineNumber: number,
      startNodeIndex: number,
      endNodeIndex: number
    ): void => {
      dispatch(setInlineCommentMode(true));
      dispatch(
        setInlineCommentReference({
          communicationId,
          lineNumber,
          nodes: [startNodeIndex, endNodeIndex],
          text: '',
        })
      );
    },
    500
  );

  useEffect(() => {
    dispatch(setInlineCommentMode(false));
  }, [dispatch, envelopeId]);

  useEffect(() => {
    const handleSelectChange = (): void => {
      const selection = document.getSelection();

      if (selection) {
        const start = selection.anchorNode;
        const startElement = start?.parentElement;
        let startSuperNode = startElement;
        if (!startElement?.id.includes('supernode')) {
          startSuperNode = startElement?.parentElement;
        }
        const startNodeIndex = startSuperNode?.id.split('-')[1];

        const end = selection.focusNode;
        const endElement = end?.parentElement;
        let endSuperNode = endElement;
        if (!endElement?.id.includes('supernode')) {
          endSuperNode = endElement?.parentElement;
        }
        const endNodeIndex = endSuperNode?.id.split('-')[1];

        let lineIndex = '';
        let communicationId = '';
        const lineNode = startSuperNode?.parentElement?.parentElement;
        if (lineNode?.id.includes('line')) {
          [, lineIndex] = lineNode.id.split('line-');
          [communicationId] = lineNode.id.split('communication-')[1].split('-line');
        }

        if (
          startNodeIndex &&
          endNodeIndex &&
          lineIndex &&
          communicationId &&
          startNodeIndex !== endNodeIndex &&
          res
        ) {
          const line = res[Number.parseInt(lineIndex, 10)];
          const sentences = line?.nodes.reduce<AnnotatedSuperNodeT[][]>((acc, cur) => {
            const curIndex = cur.nodes[0].index;
            if (curIndex === 0) {
              return [...acc, [cur]];
            }
            acc[acc.length - 1].push(cur);
            return acc;
          }, []);

          let startIndex = 0;
          let endIndex = 0;

          sentences?.forEach((s) => {
            if (
              Number.parseInt(startNodeIndex, 10) >= s[0].index &&
              Number.parseInt(startNodeIndex, 10) <= s[s.length - 1].index
            ) {
              startIndex = s[0].index;
              endIndex = s[s.length - 1].index;
            }
          });

          commentSelection(communicationId, Number.parseInt(lineIndex, 10), startIndex, endIndex);
        }
      }
    };

    document.addEventListener('selectionchange', handleSelectChange);

    return () => {
      document.removeEventListener('selectionchange', handleSelectChange);
    };
  }, [dispatch, commentSelection, res]);

  const commentsFilter = useCallback(
    (c: EnvelopeAction): boolean =>
      // @ts-ignore
      c.payload.lineIndex === commentReference.lineNumber &&
      // @ts-ignore
      c.payload.nodes?.[0] === commentReference.nodes[0] &&
      // @ts-ignore
      c.payload.communicationId === commentReference.communicationId,
    [commentReference.lineNumber, commentReference.nodes, commentReference.communicationId]
  );

  const handleSaveComment = (value?: string): void => {
    const val = value || comment.value;

    dispatch(
      addCommentEnvelope({
        envelopeId,
        value: val,
        taggedUsers: comment.taggedUsers,
        textValue: comment.text,
        lineIndex: commentReference.lineNumber,
        nodes: commentReference.nodes,
        communicationId: commentReference.communicationId,
      })
    );

    setComment({ value: '', text: '', taggedUsers: [] });
    dispatch(setCommentError(false));
  };

  const handleSetComment = (value: CommentWithUsers): void => {
    setComment(value);
  };

  const handleCancelComment = (): void => {
    dispatch(setInlineCommentMode(false));
    dispatch(
      setInlineCommentReference({ lineNumber: -1, nodes: [], text: '', communicationId: '' })
    );
  };

  const exitInlineComment = (): JSX.Element => (
    <div className="flex justify-end flex-1">
      <button
        type="button"
        className="button button--secondary h-8 w-20 justify-center"
        onClick={(): void => {
          dispatch(setInlineCommentMode(false));
          dispatch(
            setInlineCommentReference({ lineNumber: -1, nodes: [], text: '', communicationId: '' })
          );
        }}
      >
        <span className="font-bold">Exit</span>
      </button>
    </div>
  );

  if (!inlineCommentMode) return null;

  return (
    <div>
      <EnvelopeCollapsableSection
        headerTitle="Inline Comment"
        bulkReview
        extraAction={exitInlineComment()}
      >
        <div className="pt-4 flex flex-col gap-3 bg-white">
          <InlineCommentsNavigation />

          <CommentsSection
            setComment={handleSetComment}
            comment={comment}
            handleSaveComment={handleSaveComment}
            commentsFilter={commentsFilter}
            scrollOnError
            referenceText={text}
            focusOnMount
            onCancelComment={handleCancelComment}
          />
        </div>
      </EnvelopeCollapsableSection>
    </div>
  );
};

export default InlineCommentSection;
